<script lang="ts">
  import { onMount } from "svelte";

  type BIPEvent = Event & {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
  };

  const STORAGE_KEY = "takite.install.dismissed";

  let deferred = $state<BIPEvent | null>(null);
  let visible = $state(false);
  let isIOS = $state(false);
  let isStandalone = $state(false);
  let showIOSHelp = $state(false);

  function detect() {
    const ua = navigator.userAgent || "";
    isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
    // @ts-ignore - iOS Safari standalone flag
    const iosStandalone = (window.navigator as any).standalone === true;
    const dispStandalone = window.matchMedia("(display-mode: standalone)").matches;
    isStandalone = iosStandalone || dispStandalone;
  }

  function dismissedRecently(): boolean {
    const v = localStorage.getItem(STORAGE_KEY);
    if (!v) return false;
    const ts = Number(v);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < 7 * 24 * 60 * 60 * 1000;
  }

  function dismiss() {
    visible = false;
    showIOSHelp = false;
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  }

  async function install() {
    if (isIOS) {
      showIOSHelp = true;
      return;
    }
    if (!deferred) return;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") {
        visible = false;
      } else {
        dismiss();
      }
    } catch {
      /* noop */
    } finally {
      deferred = null;
    }
  }

  onMount(() => {
    detect();
    if (isStandalone || dismissedRecently()) return;

    const onBIP = (e: Event) => {
      e.preventDefault();
      deferred = e as BIPEvent;
      visible = true;
    };
    const onInstalled = () => {
      visible = false;
      deferred = null;
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);

    if (isIOS) {
      // iOS Safari nunca dispara beforeinstallprompt: mostra guia manual após 1.5s
      setTimeout(() => { if (!isStandalone) visible = true; }, 1500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  });
</script>

{#if visible}
  <div class="install-banner" role="dialog" aria-label="Instalar TáKite">
    <div class="flex items-start gap-3">
      <div class="text-3xl" aria-hidden="true">📲</div>
      <div class="flex-1 min-w-0">
        <div class="font-semibold">Instalar o TáKite no celular</div>
        <p class="text-xs text-muted mt-0.5">
          Funciona offline, abre direto da tela inicial e fica mais rápido.
        </p>
      </div>
      <button class="btn-ghost -mr-2 -mt-1" aria-label="Fechar" onclick={dismiss}>✕</button>
    </div>

    {#if showIOSHelp}
      <ol class="text-xs mt-3 space-y-1 list-decimal list-inside">
        <li>Toque em <strong>Compartilhar</strong> <span aria-hidden="true">⬆️</span> na barra do Safari.</li>
        <li>Escolha <strong>Adicionar à Tela de Início</strong>.</li>
        <li>Toque em <strong>Adicionar</strong>.</li>
      </ol>
    {:else}
      <div class="flex gap-2 mt-3">
        <button class="btn-primary flex-1" onclick={install}>
          {isIOS ? "Como instalar" : "Instalar agora"}
        </button>
        <button class="btn-secondary" onclick={dismiss}>Depois</button>
      </div>
    {/if}
  </div>
{/if}

<style>
  .install-banner {
    position: fixed;
    left: max(12px, env(safe-area-inset-left));
    right: max(12px, env(safe-area-inset-right));
    bottom: calc(12px + env(safe-area-inset-bottom));
    z-index: 60;
    background: var(--surface);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 14px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
    max-width: 480px;
    margin-left: auto;
    margin-right: auto;
  }
</style>
